import React from "react";
import { Link } from "react-router-dom";
import { RefreshCw, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PayPalMark } from "./PayPalMark";

function StripeLabel({ busy, user, trialDays, price, per }) {
  if (busy) return <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Opening checkout…</>;
  if (user) return <>Start {trialDays}-day free trial — then ${price.toFixed(2)}/{per}</>;
  return <><LogIn className="w-4 h-4 mr-2" strokeWidth={1.5} /> Sign in to start your free trial</>;
}

function PayPalOptions({ paypal, user, checkingOut, via, onSubscribe, onPass }) {
  const passPrice = (paypal.pass_price_usd || 4.99).toFixed(2);
  return (
    <>
      <Button
        data-testid="paypal-subscribe-btn"
        onClick={onSubscribe}
        disabled={checkingOut}
        variant="outline"
        className="mt-3 w-full bg-[#FFC439] hover:bg-[#F2BA36] border-transparent text-[#003087] label-caps py-6 transition-colors"
      >
        {checkingOut && via === "paypal"
          ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Opening PayPal…</>
          : <><span className="mr-2">{user ? "Subscribe with" : "Sign in & subscribe with"}</span><PayPalMark className="h-5" /></>}
      </Button>
      <p data-testid="paypal-pass-note" className="mt-4 text-center font-body text-[11px] text-parchment/50">
        Prefer no subscription?{" "}
        <button data-testid="paypal-pass-btn" onClick={onPass} disabled={checkingOut} className="text-oracle underline-offset-2 hover:underline disabled:opacity-50">
          {checkingOut && via === "pass" ? "Opening PayPal…" : `One-time 30-day pass · $${passPrice} with PayPal`}
        </button>
        {" "}— no account needed, tied to this browser.
      </p>
    </>
  );
}

export function PaywallActions({ user, paypal, checkingOut, via, trialDays, price, interval, onBuy, onPass }) {
  const per = interval === "year" ? "yr" : "mo";
  return (
    <>
      <Button
        data-testid="unlock-pass-btn"
        onClick={() => onBuy("stripe")}
        disabled={checkingOut}
        className="mt-8 w-full bg-oracle text-ink hover:bg-[#FFC033] label-caps py-6 shadow-[0_0_30px_rgba(255,176,0,0.25)] hover:shadow-[0_0_50px_rgba(255,176,0,0.5)] transition-all animate-oracle-pulse"
      >
        <StripeLabel busy={checkingOut && via === "stripe"} user={user} trialDays={trialDays} price={price} per={per} />
      </Button>
      {paypal?.enabled && <PayPalOptions paypal={paypal} user={user} checkingOut={checkingOut} via={via} onSubscribe={() => onBuy("paypal")} onPass={onPass} />}
      <p data-testid="paywall-billing-note" className="mt-4 text-center font-body text-[11px] text-parchment/40">
        Card required · no charge if you cancel before the trial ends · renews automatically at ${price.toFixed(2)}/{per} · cancel anytime from your profile.
        Secure checkout by Stripe{paypal?.enabled ? " or PayPal" : ""}.
      </p>
      <p data-testid="paywall-legal-note" className="mt-2 text-center font-body text-[11px] text-parchment/40">
        18+ only. By subscribing you agree to our{" "}
        <Link to="/legal/terms" data-testid="paywall-terms-link" className="text-parchment/60 underline-offset-2 hover:text-oracle hover:underline">Terms</Link>,{" "}
        <Link to="/legal/privacy" data-testid="paywall-privacy-link" className="text-parchment/60 underline-offset-2 hover:text-oracle hover:underline">Privacy Policy</Link> and{" "}
        <Link to="/legal/refunds" data-testid="paywall-refunds-link" className="text-parchment/60 underline-offset-2 hover:text-oracle hover:underline">Refund Policy</Link>.
      </p>
    </>
  );
}
